import { useEffect, useState } from "react";
import { Download, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, download } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { BuiltNext } from "./StageNext";

function Preview({ out }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  useEffect(() => { api.get(`/edition-outputs/${out.id}/preview`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, [out.id]);
  if (!data) return <p className="text-sm text-slate-500">Loading preview…</p>;
  const doc = `<html><head><style>${data.css} body{background:#fdfbf7;color:#1c1917;padding:24px}</style></head><body>${data.pages[page].html}</body></html>`;
  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-4" data-testid="ebook-preview">
      <div className="space-y-1 max-h-[520px] overflow-y-auto scrollbar-thin">
        {data.pages.map((p, i) => <button key={p.href} onClick={() => setPage(i)} className={`w-full text-left text-xs px-3 py-2 rounded ${i === page ? "bg-amber-500/10 text-warn-300" : "text-slate-400 hover:bg-slate-800"}`} data-testid={`ebook-page-${i}`}>{p.title}</button>)}
      </div>
      <iframe title="EPUB page" sandbox="" srcDoc={doc} className="w-full h-[520px] rounded-lg border border-slate-800 bg-[#fdfbf7]" data-testid="ebook-preview-frame" />
    </div>
  );
}

function Report({ r }) {
  return (
    <div className="divide-y divide-slate-800 border border-slate-800 rounded-lg" data-testid="ebook-report">
      {r.checks.map((c) => (
        <div key={c.id} className="p-3 flex justify-between gap-3 text-sm" data-testid={`ebook-check-${c.id}`}>
          <div><p className="text-slate-200">{c.label}</p>{c.detail && <p className="text-xs text-slate-500">{c.detail}</p>}</div><StatusBadge status={c.status} />
        </div>
      ))}
    </div>
  );
}

export default function EbookTab({ book, reload }) {
  const [outs, setOuts] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ dedication: "", epigraph: "", copyright_text: "", isbn: "", include_toc_page: true });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/books/${book.id}/ebook`).then((r) => { setOuts(r.data); setSel((s) => s || r.data[0] || null); });
  useEffect(() => { load(); }, [book.id]); // eslint-disable-line
  const ed = book.editions?.ebook || {};
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/books/${book.id}/ebook`, f); toast.success(`EPUB generated — overall ${data.report.overall}`); setSel(data); load(); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid xl:grid-cols-[360px_1fr] gap-6">
      <BuiltNext book={book} edition="ebook" />
      <div className="space-y-4">
        <div className="panel p-5 space-y-3" data-testid="ebook-setup">
          <p className="eyebrow">Kindle ebook (reflowable EPUB 3)</p>
          <p className="text-sm text-slate-400" data-testid="ebook-source">{book.approved_version_id ? "Built from your approved manuscript version." : "Approve a version in the Versions tab first."}{ed.outdated && <span className="text-warn-300"> The manuscript changed since the last EPUB — regenerate.</span>}</p>
          <div className="space-y-1"><Label className="text-xs text-slate-400">Dedication</Label><Input value={f.dedication} onChange={(e) => setF({ ...f, dedication: e.target.value })} data-testid="ebook-dedication" /></div>
          <div className="space-y-1"><Label className="text-xs text-slate-400">Epigraph</Label><Textarea value={f.epigraph} onChange={(e) => setF({ ...f, epigraph: e.target.value })} className="min-h-[60px]" data-testid="ebook-epigraph" /></div>
          <div className="space-y-1"><Label className="text-xs text-slate-400">Copyright page (blank = standard notice)</Label><Textarea value={f.copyright_text} onChange={(e) => setF({ ...f, copyright_text: e.target.value })} className="min-h-[60px]" data-testid="ebook-copyright" /></div>
          <div className="space-y-1"><Label className="text-xs text-slate-400">ISBN (optional)</Label><Input value={f.isbn} onChange={(e) => setF({ ...f, isbn: e.target.value })} data-testid="ebook-isbn" /></div>
          <label className="text-xs text-slate-400 flex gap-2"><input type="checkbox" checked={f.include_toc_page} onChange={(e) => setF({ ...f, include_toc_page: e.target.checked })} data-testid="ebook-toc-page" />Include a visible contents page</label>
          <p className="text-[11px] text-slate-500">Prologue, chapters, epilogue, author's note, acknowledgements and appendices come from your manuscript sections, in order. The navigation contents is built from the real headings.</p>
          <Button onClick={gen} disabled={busy || !book.approved_version_id} className="w-full bg-amber-600 hover:bg-amber-500" data-testid="generate-epub-button"><BookOpen size={14} className="mr-1" />{busy ? "Building & validating…" : "Generate & validate EPUB"}</Button>
        </div>
        <div className="panel p-2" data-testid="ebook-outputs">
          {outs.map((o) => (
            <button key={o.id} onClick={() => setSel(o)} className={`w-full text-left p-3 rounded-lg ${sel?.id === o.id ? "bg-slate-800" : "hover:bg-slate-800/50"}`} data-testid={`ebook-output-${o.id}`}>
              <div className="flex justify-between gap-2"><span className="text-xs font-mono truncate">{o.filename}</span><StatusBadge status={o.report.overall} /></div>
              <p className="text-[11px] text-slate-500 mt-1">v{o.version_number} · {fmtDate(o.created_at)}</p>
            </button>
          ))}
          {!outs.length && <p className="p-3 text-sm text-slate-500">No EPUBs generated yet.</p>}
        </div>
      </div>
      {sel && (
        <div className="space-y-4 min-w-0">
          <div className="panel p-5 flex flex-wrap justify-between gap-3 items-center">
            <div><p className="font-mono text-sm">{sel.filename}</p><p className="text-xs text-slate-500">{(sel.size / 1024).toFixed(1)} KB · from v{sel.version_number} · overall <b>{sel.report.overall}</b> (NOT VERIFIED items are never counted as passed)</p></div>
            <div className="flex gap-2">
              <Button variant="outline" className="border-slate-700" onClick={() => download(`/edition-outputs/${sel.id}/download`, sel.filename).catch((e) => toast.error(errMsg(e)))} data-testid="download-epub-button"><Download size={14} className="mr-1" />EPUB</Button>
              <Button variant="ghost" onClick={() => { const u = URL.createObjectURL(new Blob([JSON.stringify(sel.report, null, 2)], { type: "application/json" })); const a = document.createElement("a"); a.href = u; a.download = sel.filename.replace(".epub", "_validation.json"); a.click(); }} data-testid="download-epub-report">Report</Button>
            </div>
          </div>
          <Report r={sel.report} />
          <Preview key={sel.id} out={sel} />
        </div>
      )}
    </div>
  );
}
